import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading, isAdmin } = useAuth();
  const location = useLocation();
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>('');

  // Enhanced debugging
  useEffect(() => {
    console.log('ProtectedRoute state:', {
      user: user?.email,
      loading,
      isAdmin,
      timeoutReached
    });
  }, [user, loading, isAdmin, timeoutReached]);

  useEffect(() => {
    // Only set timeout if actually loading
    if (!loading) {
      setTimeoutReached(false);
      return;
    }

    const timeout = setTimeout(() => {
      setTimeoutReached(true);
      setDebugInfo(`Auth timeout reached. User: ${user?.email || 'none'}, Admin: ${isAdmin}, Loading: ${loading}`);
    }, 10000); // 10 seconds

    return () => clearTimeout(timeout);
  }, [user, isAdmin, loading]);

  const handleForceLogout = async () => {
    try {
      if (supabase) {
        await supabase.auth.signOut();
      }
      window.location.href = '/admin/login';
    } catch (error) {
      console.error('Error signing out:', error);
      // Force reload if signOut fails
      window.location.reload();
    }
  };

  if (loading && !timeoutReached) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-slate-400">Verifying authentication...</p>
          <p className="text-slate-600 text-sm mt-2">User: {user?.email || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (timeoutReached) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-yellow-500" />
          <h2 className="text-xl font-semibold text-white mb-2">Authentication Timeout</h2>
          <p className="text-slate-400 mb-4">
            Authentication is taking longer than expected. This might be due to a network issue or database connection problem.
          </p>
          <p className="text-xs text-slate-500 mb-4 font-mono">{debugInfo}</p>
          <button
            onClick={handleForceLogout}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Sign Out & Retry
          </button>
        </div>
      </div>
    );
  }

  // If we have a user (any authenticated user with valid role), show content
  // Granular permissions are enforced by individual pages checking user.permissions
  if (user && !loading) {
    return <>{children}</>;
  }

  // If not authenticated, redirect to login with return URL
  if (!loading && !user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // If we're here, we're still loading
  return <>{children}</>;
};

export default ProtectedRoute;
